import React, { useState, useEffect } from 'react';
import { Col, Card, CardBody, CardTitle, FormGroup, Form, Label, Row, InputGroup, InputGroupText } from 'reactstrap';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { Button, Input } from '../../components';
import { professionService } from '../../services/ProfessionAdministration';
import { toast } from 'react-toastify';
import { TabSetting } from '../../common/Types';
import Skeleton from 'react-loading-skeleton';

const SummativeMeasuresTab = (props) => {
    const [measures, setMeasures] = useState<TabSetting[]>();
    const [editing, setEditing] = useState(false);
    const { control, handleSubmit, reset, setValue, getValues } = useForm();
    const { fields, append } = useFieldArray({
        control,
        name: 'measures',
    });

    const remove = (i) => {
        const current = getValues(`measures[${i}]`);
        (setValue as any)(`measures[${i}]`, { ...current, isActive: false });
        reset(getValues());
    };

    const toggleEdit = () => {
        setEditing(!editing);
        reset({ measures: measures });
    };

    const onSubmit = (formData) => {
        const toastId = toast.info('Saving information...');
        professionService
            .saveMeasures(
                props.coa.professionId,
                formData.measures.map((measure) => new TabSetting(measure)),
            )
            .then((response) => {
                toast.update(toastId, {
                    type: 'success',
                    render: 'Information Saved.',
                });
                toggleEdit();
                setMeasures(response);
            })
            .catch(() => {
                toast.update(toastId, {
                    type: 'error',
                    render: 'Failed to save information.',
                });
            });
    };

    useEffect(() => {
        const getMeasures = () => {
            professionService
                .getMeasures(props.coa.professionId)
                .then((response) => {
                    append(response);
                    setMeasures(response);
                })
                .catch(() => {
                    toast.error('Unable to get information.');
                });
        };

        getMeasures();
    }, [append, props.coa]);

    return (
        <>
            <Col sm={6} md={5} lg={4}>
                <Card style={{ display: 'inline-block' }}>
                    <CardBody>
                        <CardTitle>
                            <h3>Summative Measures</h3>
                        </CardTitle>
                        {editing ? (
                            <Form noValidate onSubmit={handleSubmit(onSubmit)}>
                                <FormGroup>
                                    <Label>Competency Types:</Label>
                                    {fields.map((measure, i) =>
                                        JSON.parse((measure as any).isActive) ? (
                                            <div key={i}>
                                                <InputGroup className="mb-1" style={{ minWidth: 350 }}>
                                                    <Controller
                                                        name={`measures[${i}].title`}
                                                        control={control}
                                                        render={({ field }) => (
                                                            <Input
                                                                {...field}
                                                                value={field.value ?? (measure as any).title ?? ''}
                                                            />
                                                        )}
                                                    />
                                                    <InputGroupText>
                                                        <Button
                                                            className="remove"
                                                            color={'info'}
                                                            onClick={() => remove(i)}
                                                        >
                                                            <i className="remixicon-subtract-line" />
                                                        </Button>
                                                    </InputGroupText>
                                                </InputGroup>
                                                <Controller
                                                    name={`measures[${i}].professionId`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <input
                                                            type="hidden"
                                                            {...field}
                                                            value={field.value ?? (measure as any).professionId ?? ''}
                                                        />
                                                    )}
                                                />
                                                <Controller
                                                    name={`measures[${i}].isActive`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <input
                                                            type="hidden"
                                                            {...field}
                                                            value={field.value ?? (measure as any).isActive ?? ''}
                                                        />
                                                    )}
                                                />
                                                <Controller
                                                    name={`measures[${i}].customId`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <input
                                                            type="hidden"
                                                            {...field}
                                                            value={field.value ?? (measure as any).customId ?? ''}
                                                        />
                                                    )}
                                                />
                                            </div>
                                        ) : (
                                            <div key={i}>
                                                <Controller
                                                    name={`measures[${i}].title`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <input
                                                            type="hidden"
                                                            {...field}
                                                            value={field.value ?? (measure as any).title ?? ''}
                                                        />
                                                    )}
                                                />
                                                <Controller
                                                    name={`measures[${i}].professionId`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <input
                                                            type="hidden"
                                                            {...field}
                                                            value={field.value ?? (measure as any).professionId ?? ''}
                                                        />
                                                    )}
                                                />
                                                <Controller
                                                    name={`measures[${i}].isActive`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <input
                                                            type="hidden"
                                                            {...field}
                                                            value={field.value ?? (measure as any).isActive ?? ''}
                                                        />
                                                    )}
                                                />
                                                <Controller
                                                    name={`measures[${i}].customId`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <input
                                                            type="hidden"
                                                            {...field}
                                                            value={field.value ?? (measure as any).customId ?? ''}
                                                        />
                                                    )}
                                                />
                                            </div>
                                        ),
                                    )}
                                    <Button
                                        className="add"
                                        color={'info'}
                                        onClick={() =>
                                            append({
                                                professionId: props.coa.professionId,
                                                customId: 0,
                                                isActive: true,
                                                title: '',
                                            })
                                        }
                                    >
                                        <i className="remixicon-add-line" />
                                        <span className="ml-1">Add Competency Type</span>
                                    </Button>
                                </FormGroup>
                                <Row className="mt-3">
                                    <Col>
                                        <Button type="submit">Save</Button>&nbsp;
                                        <Button outline onClick={() => toggleEdit()}>
                                            Cancel
                                        </Button>
                                    </Col>
                                </Row>
                            </Form>
                        ) : (
                            <>
                                <Label>Competency Types:</Label>
                                {measures ? (
                                    <>
                                        {measures.map((measure, i) => {
                                            return (
                                                <Row key={i}>
                                                    <Col className="competency-title">{(measure as any).title}</Col>
                                                </Row>
                                            );
                                        })}
                                        <Row className="mt-3">
                                            <Col>
                                                <Button onClick={() => toggleEdit()}>Edit</Button>
                                            </Col>
                                        </Row>
                                    </>
                                ) : (
                                    <Skeleton count={5} />
                                )}
                            </>
                        )}
                    </CardBody>
                </Card>
            </Col>
        </>
    );
};

export default SummativeMeasuresTab;
